import * as React from 'react';
import html2canvas from 'html2canvas';
import { t } from '../../services/HelperService';
import { toPromptImageDataUrl } from './AccentPromptComposer';

const MIN_SIZE = 8;
const DRAG_THRESHOLD = 3;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const normalizeRect = (a, b) => ({
  x: Math.min(a.x, b.x),
  y: Math.min(a.y, b.y),
  w: Math.abs(a.x - b.x),
  h: Math.abs(a.y - b.y),
});

/**
 * The PDF page (react-pdf sets data-page-number on each page root) the snip
 * rectangle overlaps most, or null for HTML previews. Rect is in host client space.
 */
const pageUnderRect = (host, rect) => {
  const hostBox = host.getBoundingClientRect();
  let best = null;
  let bestArea = 0;
  host.querySelectorAll('[data-page-number]').forEach((el) => {
    const b = el.getBoundingClientRect();
    const left = Math.max(b.left - hostBox.left, rect.x);
    const top = Math.max(b.top - hostBox.top, rect.y);
    const right = Math.min(b.right - hostBox.left, rect.x + rect.w);
    const bottom = Math.min(b.bottom - hostBox.top, rect.y + rect.h);
    const area = Math.max(0, right - left) * Math.max(0, bottom - top);
    if (area > bestArea) {
      bestArea = area;
      best = parseInt(el.getAttribute('data-page-number'), 10) || null;
    }
  });
  return best;
};

/**
 * Rasterises a rectangle of the host element (host client coordinates) into a prompt
 * image data URL. html2canvas clones the document, restores scroll positions, copies
 * existing <canvas> pixels (react-pdf pages) and paints the DOM (grids, cards, tables),
 * so one path serves every preview.
 *
 * x/y are HOST-RELATIVE: html2canvas 1.4 adds them to the element's own document
 * position (renderOptions.x = opts.x + left, where left is the cloned element's
 * bounding rect + scroll), unlike the 1.0 docs that call them document coordinates.
 * Passing document coordinates here shifted every snip by the host's distance from
 * the page corner — i.e. by the dialog's position.
 */
export const captureRegion = async (host, rect) => {
  const scale = Math.min(window.devicePixelRatio || 1, 2);
  const canvas = await html2canvas(host, {
    x: Math.round(rect.x),
    y: Math.round(rect.y),
    width: Math.max(1, Math.round(rect.w)),
    height: Math.max(1, Math.round(rect.h)),
    scale,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    ignoreElements: (el) => el?.dataset?.snipIgnore === 'true',
  });
  return toPromptImageDataUrl(canvas, canvas.width, canvas.height);
};

/**
 * Region snip over any preview: RIGHT-button drag (or left drag while snipMode is on)
 * draws a marquee; on release the covered pixels become a prompt image via onCapture.
 * Lets a user point the assistant at exactly the part of the preview that is wrong,
 * in every session profile, without leaving the dialog.
 *
 * Props:
 * - onCapture({ dataUrl, rect, page }): the downscaled PNG/JPEG data URL, the host-
 *   relative rectangle and (PDF previews) the page number the snip mostly covers.
 * - onError(err): capture failure (never thrown into React).
 * - snipMode / onSnipModeChange(bool): optional controlled left-drag mode, toggled by a
 *   camera button in the host's tab strip for discoverability / trackpads; it switches
 *   itself off after one snip or Escape.
 * - disabled: no snipping (e.g. while the preview is rendering).
 * - style: extra style for the wrapper (it is position: relative and fills its parent).
 *
 * The browser context menu is suppressed inside the wrapper (right-drag would otherwise
 * open it on release); previews have nothing that needs it.
 */
export const AccentRegionSnip = ({ children, onCapture, onError, snipMode, onSnipModeChange, disabled, style }) => {
  const hostRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const [rect, setRect] = React.useState(null);
  const [capturing, setCapturing] = React.useState(false);

  const pointInHost = React.useCallback((e) => {
    const box = hostRef.current.getBoundingClientRect();
    return { x: clamp(e.clientX - box.left, 0, box.width), y: clamp(e.clientY - box.top, 0, box.height) };
  }, []);

  const endSnipMode = React.useCallback(() => {
    if (snipMode && typeof onSnipModeChange === 'function') onSnipModeChange(false);
  }, [snipMode, onSnipModeChange]);

  const capture = React.useCallback(async (r) => {
    const host = hostRef.current;
    if (!host) return;
    setCapturing(true);
    try {
      // let the marquee unmount before the clone is taken
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      const dataUrl = await captureRegion(host, r);
      if (!dataUrl) throw new Error('capture produced no image');
      onCapture?.({ dataUrl, rect: r, page: pageUnderRect(host, r) });
    } catch (err) {
      onError?.(err);
    } finally {
      setCapturing(false);
    }
  }, [onCapture, onError]);

  const onMouseDown = (e) => {
    if (disabled || capturing || !hostRef.current) return;
    const rightDrag = e.button === 2;
    const leftSnip = e.button === 0 && !!snipMode;
    if (!rightDrag && !leftSnip) return;
    e.preventDefault();
    e.stopPropagation();

    const start = pointInHost(e);
    dragRef.current = { start, moved: false };
    setRect({ ...start, w: 0, h: 0 });

    const cleanup = () => {
      window.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('mouseup', onUp, true);
      window.removeEventListener('keydown', onKey, true);
    };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const p = pointInHost(ev);
      if (Math.abs(p.x - d.start.x) > DRAG_THRESHOLD || Math.abs(p.y - d.start.y) > DRAG_THRESHOLD) d.moved = true;
      setRect(normalizeRect(d.start, p));
    };
    const onKey = (ev) => {
      if (ev.key !== 'Escape') return;
      cleanup();
      dragRef.current = null;
      setRect(null);
      endSnipMode();
    };
    const onUp = (ev) => {
      cleanup();
      const d = dragRef.current;
      dragRef.current = null;
      setRect(null);
      if (!d) return;
      const r = normalizeRect(d.start, pointInHost(ev));
      if (d.moved && r.w >= MIN_SIZE && r.h >= MIN_SIZE) {
        ev.preventDefault();
        capture(r);
      }
      endSnipMode();
    };
    window.addEventListener('mousemove', onMove, true);
    window.addEventListener('mouseup', onUp, true);
    window.addEventListener('keydown', onKey, true);
  };

  const active = !!rect || capturing;

  return (
    <div
      ref={hostRef}
      data-tagname="regionSnip"
      onMouseDown={onMouseDown}
      onContextMenu={(e) => e.preventDefault()}
      title={disabled ? undefined : t('Right-drag to snip part of the preview into the prompt')}
      style={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', // the children scroll; this keeps the marquee's dimming shadow inside the preview
        cursor: snipMode ? 'crosshair' : undefined,
        userSelect: active || snipMode ? 'none' : undefined,
        ...(style ?? {}),
      }}
    >
      {children}
      {snipMode && !active && (
        <div
          data-snip-ignore="true"
          style={{
            position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 20,
            background: 'rgba(111, 66, 193, 0.92)', color: '#fff', fontSize: 12, padding: '4px 10px', borderRadius: 12,
            pointerEvents: 'none', whiteSpace: 'nowrap',
          }}
        >
          {t('Drag over the part of the preview to send (Esc to cancel)')}
        </div>
      )}
      {rect && (
        <>
          <div data-snip-ignore="true" style={{ position: 'absolute', inset: 0, zIndex: 19, cursor: 'crosshair' }} />
          <div
            data-snip-ignore="true"
            style={{
              position: 'absolute', zIndex: 21, pointerEvents: 'none',
              left: rect.x, top: rect.y, width: rect.w, height: rect.h,
              border: '2px solid #6f42c1', background: 'rgba(111, 66, 193, 0.12)',
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.18)',
            }}
          />
        </>
      )}
      {capturing && (
        <div
          data-snip-ignore="true"
          style={{
            position: 'absolute', top: 8, right: 8, zIndex: 21, background: 'rgba(0,0,0,0.65)', color: '#fff',
            fontSize: 12, padding: '3px 8px', borderRadius: 4, pointerEvents: 'none',
          }}
        >
          {t('Capturing...')}
        </div>
      )}
    </div>
  );
};
